'use client'

import * as React from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Search, Send } from 'lucide-react'
import { z } from 'zod/v4'

import type { ShareEntityType } from '@package/drizzle/schema'
import type { RenderLink } from '@ui/web/utils'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldSet,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Skeleton,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { tryCatch } from '@helper/utils/core'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { BookGalleryCard } from '../components/book-gallery-card'
import { BookCoverCard, LessonModuleCard } from '../components/cards'
import { useAuthClient, useSharedTRPC } from '../context'
import { SchoolClassSelect } from './school-class-select'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Cover of the thing being shared. A union rather than a `ReactNode` slot so every
 * call site gets the same sizing, and so Storybook can enumerate the variants.
 */
export type ShareContentCover =
  | { kind: 'book'; coverUrl: string }
  /** A user-authored collection, which has no cover of its own — stacks its member books. */
  | { kind: 'collection'; books: { book: { id: string; coverUrl: string } }[] }
  /** A curated catalogue collection, which does have its own 5:4 cover art. */
  | { kind: 'bookCollection'; coverUrl: string }
  | { kind: 'lessonModule'; id: string; coverUrl: string | null }

export interface ShareRecipientOption {
  id: string
  name: string
  image?: string | null
  /** Row subtitle. `null`/absent renders "Tanpa kelas". */
  schoolClassName?: string | null
}

/**
 * Which body the dialog renders.
 *
 * `no-class` and `no-student` are distinct because the remedy differs: a member has to
 * be assigned to a class by an admin, while an admin has to add children first.
 */
export type ShareContentDialogState = 'ready' | 'loading' | 'error' | 'no-class' | 'no-student'

export interface ShareContentDialogBaseProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  cover: ShareContentCover
  title?: string
  description?: string
  state: ShareContentDialogState
  recipients: ShareRecipientOption[]
  selectedIds: string[]
  onSelectedIdsChange: (ids: string[]) => void
  search: string
  onSearchChange: (search: string) => void
  /** Class filter. Connected callers pass `SchoolClassSelect`; Storybook passes the Base. */
  classSelect?: React.ReactNode
  isSubmitting?: boolean
  onSubmit: () => void
  onRetry?: () => void
  /** Link out to "add a child" for the `no-student` state. */
  renderAddChildLink?: RenderLink
  submitLabel?: string
  errorMessage?: string
  /** What the recipients are called: `anak` in a family, `murid` in a school. */
  recipientNoun?: 'murid' | 'anak'
}

// ---------------------------------------------------------------------------
// Internal pieces
// ---------------------------------------------------------------------------

/** Also used by `share-recipients-dialog`, so both share dialogs size a cover identically. */
export function CoverPreview({ cover }: { cover: ShareContentCover }) {
  if (cover.kind === 'collection') {
    return <BookGalleryCard collection={cover.books} withAnimation={false} className='srd:w-[140px]' imageSize={140} />
  }

  if (cover.kind === 'bookCollection') {
    // Matches the aspect ratio and radius the detail page uses for the same artwork.
    return (
      <img
        src={cover.coverUrl}
        alt=''
        width={175}
        height={140}
        draggable={false}
        className='srd:aspect-5/4 srd:w-[175px] srd:rounded-lg srd:object-cover'
      />
    )
  }

  if (cover.kind === 'lessonModule') {
    return <LessonModuleCard id={cover.id} coverUrl={cover.coverUrl} size='small' className='srd:h-[120px] srd:w-40' />
  }

  return <BookCoverCard coverUrl={cover.coverUrl} size='small' className='srd:h-[140px] srd:w-[105px]' />
}

function RecipientRow({
  recipient,
  checked,
  onCheckedChange,
}: {
  recipient: ShareRecipientOption
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <label
      className={cn(
        'srd:border-outline-variant srd:flex srd:w-full srd:min-w-0 srd:cursor-pointer srd:items-center srd:justify-between srd:gap-x-4 srd:border-b srd:px-1 srd:py-3 srd:select-none',
      )}
    >
      <div className='srd:flex srd:w-full srd:min-w-0 srd:items-center srd:gap-x-3'>
        <Avatar profile='user' className='srd:size-10 srd:shrink-0'>
          <AvatarImage src={recipient.image ?? undefined} alt='' />
          <AvatarFallback>{getInitials(recipient.name)}</AvatarFallback>
        </Avatar>

        <div className='srd:flex srd:min-w-0 srd:flex-col'>
          <p className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-black srd:truncate')}>
            {recipient.name}
          </p>
          <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100 srd:truncate')}>
            {recipient.schoolClassName ?? 'Tanpa kelas'}
          </p>
        </div>
      </div>

      <Checkbox className='srd:shrink-0' checked={checked} onCheckedChange={(value) => onCheckedChange(!!value)} />
    </label>
  )
}

function EmptyBody({ title, description, action }: { title: string; description: string; action?: React.ReactNode }) {
  return (
    <div className='srd:flex srd:min-h-0 srd:flex-1 srd:flex-col srd:items-center srd:gap-y-2 srd:overflow-y-auto srd:py-10 srd:text-center'>
      <p className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-black')}>{title}</p>
      <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>{description}</p>
      {action}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Presentational base — no data fetching, safe to use in Storybook
// ---------------------------------------------------------------------------

export function ShareContentDialogBase({
  open,
  onOpenChange,
  cover,
  title = 'Bagikan',
  description = 'Pilih siapa saja yang ingin kamu rekomendasikan materi ini.',
  state,
  recipients,
  selectedIds,
  onSelectedIdsChange,
  search,
  onSearchChange,
  classSelect,
  isSubmitting = false,
  onSubmit,
  onRetry,
  renderAddChildLink,
  submitLabel = 'Bagikan',
  errorMessage,
  recipientNoun = 'murid',
}: ShareContentDialogBaseProps) {
  // Filtering is client-side on purpose: the recipient list is fetched with
  // `pageSize: 'all'`, so a round trip per keystroke would buy nothing.
  const filtered = search
    ? recipients.filter((recipient) => recipient.name.toLowerCase().includes(search.toLowerCase()))
    : recipients

  const allFilteredSelected = filtered.length > 0 && filtered.every((recipient) => selectedIds.includes(recipient.id))

  function toggleAll() {
    if (allFilteredSelected) {
      const filteredIds = new Set(filtered.map((recipient) => recipient.id))
      onSelectedIdsChange(selectedIds.filter((id) => !filteredIds.has(id)))
      return
    }
    onSelectedIdsChange([...new Set([...selectedIds, ...filtered.map((recipient) => recipient.id)])])
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='srd:flex srd:max-h-[90dvh] srd:w-full srd:max-w-125 srd:flex-col srd:overflow-y-auto srd:rounded-2xl'>
        {/*
          Everything except the recipient list is `shrink-0`, so the list is the only part
          that gives up space when the dialog hits its height cap. Without this the footer
          (flex-shrink defaults to 1) gets squeezed and the last row is clipped behind it.
        */}
        <DialogHeader className='srd:flex srd:shrink-0 srd:flex-col srd:items-center srd:gap-y-2'>
          <CoverPreview cover={cover} />
          <DialogTitle className={cn(typographyVariants({ variant: 'headline-sm' }), 'srd:text-neutral-black')}>
            {title}
          </DialogTitle>
          <DialogDescription className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>
            {description}
          </DialogDescription>
        </DialogHeader>

        {state === 'error' ? (
          <EmptyBody
            title='Oops, Terjadi Kesalahan'
            description='Gagal memuat daftar penerima.'
            action={
              onRetry && (
                <Button variant='outline' size='sm' className='srd:rounded-full' onClick={onRetry}>
                  Coba lagi
                </Button>
              )
            }
          />
        ) : state === 'no-class' ? (
          <EmptyBody
            title='Belum ada kelas'
            description='Kamu belum ditugaskan mengampu di kelas mana pun di sekolah ini, jadi belum ada murid yang bisa kamu beri rekomendasi.'
          />
        ) : state === 'no-student' ? (
          <EmptyBody
            title={`Belum ada ${recipientNoun}`}
            description={`Tambahkan ${recipientNoun} terlebih dahulu supaya kamu bisa membagikan materi ini.`}
            action={
              renderAddChildLink && (
                <Button variant='outline' size='sm' className='srd:rounded-full' asChild>
                  {renderAddChildLink({ children: `Tambahkan ${recipientNoun}` })}
                </Button>
              )
            }
          />
        ) : (
          <>
            <div className='srd:flex srd:w-full srd:shrink-0 srd:items-center srd:gap-x-2'>
              {classSelect && <div className='srd:w-[45%] srd:min-w-0'>{classSelect}</div>}

              <InputGroup className='srd:min-w-0 srd:flex-1 srd:rounded-full srd:data-[size=default]:h-8'>
                <InputGroupAddon align='inline-start'>
                  <Search className='srd:text-blue-100 srd:size-4' />
                </InputGroupAddon>
                <InputGroupInput
                  placeholder='Cari'
                  value={search}
                  onChange={(event) => onSearchChange(event.target.value)}
                  type='search'
                />
              </InputGroup>
            </div>

            <div className='srd:border-outline-variant srd:flex srd:w-full srd:shrink-0 srd:items-center srd:justify-between srd:border-b srd:pb-2'>
              <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-blue-100')}>
                {selectedIds.length} terpilih
              </p>
              <Checkbox
                checked={allFilteredSelected}
                onCheckedChange={toggleAll}
                disabled={filtered.length === 0}
                aria-label={allFilteredSelected ? 'Batal pilih semua' : 'Pilih semua'}
              />
            </div>

            {state === 'loading' ? (
              <div className='srd:flex srd:w-full srd:shrink-0 srd:flex-col srd:gap-y-2'>
                {[0, 1, 2, 3].map((index) => (
                  <Skeleton key={index} className='srd:h-16 srd:w-full srd:rounded-lg' />
                ))}
              </div>
            ) : (
              /*
                The scroll box is this div and the `<fieldset>` sits *inside* it, never the
                other way round: Blink neither clips a fieldset's overflow nor propagates a
                flex height constraint into it, so a scrollable fieldset spills its rows over
                the footer instead of scrolling them.
                `flex-1` lets the list absorb the leftover height rather than pushing the
                footer past the dialog's cap, and the `min-h` floor keeps it from collapsing to
                0px on short viewports (a scroll container's automatic minimum size is 0).
              */
              <div className='srd:flex srd:min-h-32 srd:w-full srd:min-w-0 srd:flex-1 srd:flex-col srd:overflow-x-hidden srd:overflow-y-auto'>
                <FieldSet className='srd:flex srd:w-full srd:min-w-0 srd:flex-col srd:gap-0' disabled={isSubmitting}>
                  {filtered.length === 0 ? (
                    <p
                      className={cn(
                        typographyVariants({ variant: 'body-sm' }),
                        'srd:text-grey-100 srd:py-6 srd:text-center',
                      )}
                    >
                      {search
                        ? 'Tidak ada nama yang cocok dengan pencarian.'
                        : `Tidak ada ${recipientNoun} di kelas ini.`}
                    </p>
                  ) : (
                    filtered.map((recipient) => (
                      <RecipientRow
                        key={recipient.id}
                        recipient={recipient}
                        checked={selectedIds.includes(recipient.id)}
                        onCheckedChange={(checked) =>
                          onSelectedIdsChange(
                            checked ? [...selectedIds, recipient.id] : selectedIds.filter((id) => id !== recipient.id),
                          )
                        }
                      />
                    ))
                  )}
                </FieldSet>
              </div>
            )}
          </>
        )}

        {errorMessage && (
          <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-error')}>{errorMessage}</p>
        )}

        {/*
          No `mx-0`/`mb-0` override here: `DialogFooter` carries `-mx-6 -mb-6` so its bar
          bleeds to the dialog edges, and cancelling those leaves it floating inside the
          padding. `rounded-b-2xl` matches this dialog's radius (the footer's own default
          is the 28px of the unstyled DialogContent).
        */}
        <DialogFooter className='srd:shrink-0 srd:flex-row srd:items-center srd:justify-end srd:gap-x-3 srd:rounded-b-2xl'>
          <Button variant='outline' className='srd:rounded-full' onClick={() => onOpenChange(false)}>
            Tutup
          </Button>
          <Button
            className='srd:rounded-full'
            onClick={onSubmit}
            loading={isSubmitting}
            disabled={isSubmitting || selectedIds.length === 0 || state !== 'ready'}
          >
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Connected component
// ---------------------------------------------------------------------------

const formSchema = z.object({
  recipientIds: z.array(z.string()).min(1, 'Pilih setidaknya satu penerima'),
})

export interface ShareContentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  entityType: ShareEntityType
  entityId: string
  cover: ShareContentCover
  onSuccess?: () => void | Promise<void>
  renderAddChildLink?: RenderLink
}

/**
 * Recipient picker plus `share.create`. Recipient scoping is enforced server-side by
 * `myOrganization.listChildren`, which restricts a member to their own classes and
 * rejects the "Tanpa kelas" sentinel outright — this component only mirrors that in the UI.
 */
export function ShareContentDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
  cover,
  onSuccess,
  renderAddChildLink,
}: ShareContentDialogProps) {
  const trpc = useSharedTRPC()
  const authClient = useAuthClient()
  const queryClient = useQueryClient()

  const { data: session } = authClient.useSession()
  const { data: memberRole } = authClient.useActiveMemberRole()
  const { data: activeOrg } = authClient.useActiveOrganization()
  const isAdminOrOwner = memberRole?.role === 'owner' || memberRole?.role === 'admin'

  const [search, setSearch] = React.useState('')
  const [teamId, setTeamId] = React.useState<string | null>(null)

  const childrenQuery = useQuery({
    ...trpc.myOrganization.listChildren.queryOptions({
      pageSize: 'all',
      teamId: teamId ?? undefined,
      includeSchoolClass: true,
    }),
    enabled: open,
    placeholderData: (prev) => prev,
  })

  // Same pair of queries `SchoolClassSelect` runs, so React Query dedupes them by key.
  // The count decides two things: whether a class filter is meaningful at all (a family
  // organization has no classes), and whether an empty list means "no class" or "no student".
  const allTeamsQuery = useQuery({
    ...trpc.myOrganization.getAllTeams.queryOptions(),
    enabled: open && isAdminOrOwner,
  })

  const myTeamsQuery = useQuery({
    ...trpc.myOrganization.getMyTeams.queryOptions(),
    enabled: open && !isAdminOrOwner,
  })

  const teamCount = isAdminOrOwner ? (allTeamsQuery.data?.length ?? 0) : (myTeamsQuery.data?.length ?? 0)

  const recipients: ShareRecipientOption[] = (childrenQuery.data?.data ?? []).map((child) => ({
    id: child.id,
    name: child.name,
    image: child.image,
    schoolClassName: child.schoolClass?.name ?? null,
  }))

  const { mutateAsync: createShare } = useMutation(trpc.share.create.mutationOptions())

  const form = useForm({
    defaultValues: { recipientIds: [] } as z.input<typeof formSchema>,
    validators: { onSubmit: formSchema },
    onSubmit: async ({ value, formApi }) => {
      const [, error] = await tryCatch(
        createShare({
          entityType,
          entityId,
          recipientIds: value.recipientIds,
          organizationId: session?.session.activeOrganizationId ?? null,
        }),
      )

      if (error) {
        toast.error('Gagal membagikan materi')
        formApi.setErrorMap({
          // @ts-expect-error - TanStack Form errorMap accepts string for onSubmit
          onSubmit: error instanceof Error ? error.message : 'Terjadi kesalahan',
        })
        return
      }

      toast.success('Materi berhasil dibagikan!')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: trpc.notification.unreadCount.queryKey() }),
        // `infiniteQueryKey`, not `queryKey`: the feed reads the procedure as an infinite
        // query, and the two key shapes differ by `type` — a `'query'` key matches nothing
        // the feed ever cached, so the sender's own ACTOR row never showed up.
        queryClient.invalidateQueries({ queryKey: trpc.notification.list.infiniteQueryKey() }),
      ])

      formApi.reset()
      onOpenChange(false)
      await onSuccess?.()
    },
  })

  const recipientIds = useStore(form.store, (state) => state.values.recipientIds)
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting)
  const errorMap = useStore(form.store, (state) => state.errorMap)

  const state: ShareContentDialogState = (() => {
    if (childrenQuery.isError) return 'error'
    if (childrenQuery.isPending) return 'loading'
    // A member with no class has nobody to share with, and no filter can change that.
    if (!isAdminOrOwner && teamCount === 0) return 'no-class'
    // Only claim "no student" for the unfiltered org view; an empty class is a normal
    // `ready` list with an inline note, so the user can switch class.
    if (isAdminOrOwner && teamId === null && recipients.length === 0) return 'no-student'
    return 'ready'
  })()

  return (
    <ShareContentDialogBase
      open={open}
      onOpenChange={onOpenChange}
      cover={cover}
      state={state}
      recipients={recipients}
      selectedIds={recipientIds}
      onSelectedIdsChange={(ids) => form.setFieldValue('recipientIds', ids)}
      search={search}
      onSearchChange={setSearch}
      classSelect={
        // A family organization has no classes; a filter with one possible value is noise.
        // An empty school falls through to the `no-student` body, which says so in words.
        teamCount > 0 ? <SchoolClassSelect value={teamId} onValueChange={setTeamId} size='sm' showNoClass /> : undefined
      }
      isSubmitting={isSubmitting}
      onSubmit={() => void form.handleSubmit()}
      onRetry={() => void childrenQuery.refetch()}
      renderAddChildLink={renderAddChildLink}
      errorMessage={typeof errorMap?.onSubmit === 'string' ? errorMap.onSubmit : undefined}
      recipientNoun={activeOrg?.type.includes('family') ? 'anak' : 'murid'}
    />
  )
}

// ---------------------------------------------------------------------------
// Trigger
// ---------------------------------------------------------------------------

export interface ShareContentButtonProps extends Omit<ShareContentDialogProps, 'open' | 'onOpenChange'> {
  className?: string
  label?: string
}

/**
 * Icon trigger plus the dialog. Renders nothing for a child account: `canNotify` gates
 * child-to-child notifications, so a share button there would only ever fail.
 */
export function ShareContentButton({ className, label = 'Bagikan', ...dialogProps }: ShareContentButtonProps) {
  const authClient = useAuthClient()
  const { data: session } = authClient.useSession()
  const [open, setOpen] = React.useState(false)

  if (session?.user.userType !== 'main') return null

  return (
    <>
      <Button
        variant='ghost'
        size='icon'
        className={cn('srd:shrink-0', className)}
        onClick={() => setOpen(true)}
        title={label}
      >
        <Send className='srd:text-neutral-black srd:size-6' />
      </Button>

      {open && <ShareContentDialog {...dialogProps} open={open} onOpenChange={setOpen} />}
    </>
  )
}
